import { get } from 'lodash';
import { INITIAL_BOOKING_ROOT_KEY } from '../../../constants';
import getValidator from '../../../utils/CreateAccountValidation';
import { RULE_STATUS } from '../../../utils/CreateAccountValidation/constants';
import {
    birthDayField,
    birthMonthField,
    birthYearField,
    confirmEmailField,
    emailField,
    firstNameField,
    genderField,
    lastNameField,
    passwordField,
    SUBMIT_MODE,
    termsOfServiceField,
} from '../constants';
import {
    trackError,
    trackEmailInUseError,
    trackRadio,
    trackFocus,
    trackV2PasswordError,
    trackPasswordModalShow,
    trackClickExpressSubmit,
    trackAccountCreationFailure,
} from './tracking';

export const CREATE_ACCOUNT_START = '@@createaccountform/CREATE_ACCOUNT_START';
export const CREATE_ACCOUNT_SUCCESS =
    '@@createaccountform/CREATE_ACCOUNT_SUCCESS';
export const CREATE_ACCOUNT_FIELD_ERROR =
    '@@createaccountform/CREATE_ACCOUNT_FIELD_ERROR';
export const CREATE_ACCOUNT_FAILURE =
    '@@createaccountform/CREATE_ACCOUNT_FAILURE';
export const CLEAR_EMAIL_ERROR = '@@createaccountform/CLEAR_EMAIL_ERROR';
export const CLEAR_CONFIRM_EMAIL_VALIDATION_ERROR =
    '@@createaccountform/CLEAR_CONFIRM_EMAIL_VALIDATION_ERROR';

export const CREATE_ACCOUNT_SUBMIT =
    '@@createaccountform/CREATE_ACCOUNT_FORM_SUBMIT';
export const CREATE_ACCOUNT_FORM_CHANGED =
    '@@createaccountform/CREATE_ACCOUNT_FORM_CHANGED';
export const CREATE_ACCOUNT_VALIDATION_ERROR =
    '@@createaccountform/CREATE_ACCOUNT_VALIDATION_ERROR';

export const CREATE_ACCOUNT_INLINE_EMAIL_START =
    '@@createaccountform/CREATE_ACCOUNT_FORM_INLINE_EMAIL_START';

export const CREATE_ACCOUNT_INLINE_EMAIL_END =
    '@@createaccountform/CREATE_ACCOUNT_FORM_INLINE_EMAIL_END';

export const CREATE_ACCOUNT_EMAIL_TAKEN =
    '@@createaccountform/CREATE_ACCOUNT_FORM_INLINE_EMAIL_TAKEN';

export const CREATE_ACCOUNT_INLINE_EMAIL_CLEAR =
    '@@createaccountform/CREATE_ACCOUNT_FORM_INLINE_EMAIL_CLEAR';

export const CREATE_ACCOUNT_SHOW_PASSWORD_MODAL =
    '@@createaccountform/CREATE_ACCOUNT_FORM_SHOW_PASSWORD_MODAL';

export const CREATE_ACCOUNT_CLOSE_PASSWORD_MODAL =
    '@@createaccountform/CREATE_ACCOUNT_FORM_CLOSE_PASSWORD_MODAL';

export const CREATE_ACCOUNT_FORM_SHOW_LOGIN_MODAL =
    '@@createaccountform/CREATE_ACCOUNT_FORM_SHOW_LOGIN_MODAL';
export const CREATE_ACCOUNT_FORM_CLOSE_LOGIN_MODAL =
    '@@createaccountform/CREATE_ACCOUNT_FORM_CLOSE_LOGIN_MODAL';

export const CREATE_ACCOUNT_FORM_PASSWORD_STATUS_CHECK =
    '@@createaccountform/CREATE_ACCOUNT_FORM_PASSWORD_STATUS_CHECK';

export const CREATE_ACCOUNT_FORM_SHOW_INLINE_PASSWORD_STATUS_CHECK =
    '@@createaccontform/CREATE_ACCOUNT_FORM_SHOW_PASSWORD_STATUS_CHECK';

export const CreateAccountActions = {
    CLEAR_EMAIL_ERROR,
    CLEAR_CONFIRM_EMAIL_VALIDATION_ERROR,
    CREATE_ACCOUNT_START,
    CREATE_ACCOUNT_SUCCESS,
    CREATE_ACCOUNT_FIELD_ERROR,
    CREATE_ACCOUNT_FAILURE,
    CREATE_ACCOUNT_SUBMIT,
    CREATE_ACCOUNT_FORM_CHANGED,
    CREATE_ACCOUNT_VALIDATION_ERROR,
    CREATE_ACCOUNT_INLINE_EMAIL_START,
    CREATE_ACCOUNT_INLINE_EMAIL_END,
    CREATE_ACCOUNT_EMAIL_TAKEN,
    CREATE_ACCOUNT_CLOSE_PASSWORD_MODAL,
    CREATE_ACCOUNT_SHOW_PASSWORD_MODAL,
    CREATE_ACCOUNT_INLINE_EMAIL_CLEAR,
    CREATE_ACCOUNT_FORM_SHOW_LOGIN_MODAL,
    CREATE_ACCOUNT_FORM_CLOSE_LOGIN_MODAL,
    CREATE_ACCOUNT_FORM_PASSWORD_STATUS_CHECK,
    CREATE_ACCOUNT_FORM_SHOW_INLINE_PASSWORD_STATUS_CHECK,
};

export const EMAIL_TAKEN_MESSAGE = 'That email is already in use.';

const apiHost = process.env.API_HOST || '';
const standardEndpoint = `${apiHost}/api/web/2/patient`;

export const clearEmailError = () => ({
    type: CreateAccountActions.CLEAR_EMAIL_ERROR,
});

export const clearConfirmEmailVaidationError = () => ({
    type: CreateAccountActions.CLEAR_CONFIRM_EMAIL_VALIDATION_ERROR,
});

export const createAccountStart = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_START,
});

export const createAccountSubmit = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_SUBMIT,
});

export const createAccountFormChanged = (field, value) => ({
    type: CreateAccountActions.CREATE_ACCOUNT_FORM_CHANGED,
    field,
    value,
});

export const createAccountValidationError = (field, value) => ({
    type: CreateAccountActions.CREATE_ACCOUNT_VALIDATION_ERROR,
    field,
    value,
});

export const createAccountInlineEmailValidationStart = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_INLINE_EMAIL_START,
});
export const createAccountInlineEmailValidationEnd = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_INLINE_EMAIL_END,
});

export const createAccountEmailValidationTaken = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_EMAIL_TAKEN,
});

export const createAccountInlineEmailValidationClear = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_INLINE_EMAIL_CLEAR,
});

export const createAccountFormShowPasswordModal = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_SHOW_PASSWORD_MODAL,
});

export const createAccountFormClosePasswordModal = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_CLOSE_PASSWORD_MODAL,
});

export const createAccountFormShowLoginModal = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_FORM_SHOW_LOGIN_MODAL,
});

export const createAccountFormCloseLoginModal = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_FORM_CLOSE_LOGIN_MODAL,
});

export const createAccountFormPasswordStatusCheck = (statusCheck) => ({
    type: CreateAccountActions.CREATE_ACCOUNT_FORM_PASSWORD_STATUS_CHECK,
    statusCheck,
});

export const createAccountFormShowInlinePasswordStatusCheck = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_FORM_SHOW_INLINE_PASSWORD_STATUS_CHECK,
});

export const createAccountSuccess = (
    email,
    password,
    rememberMe,
    responseBody
) => ({
    type: CreateAccountActions.CREATE_ACCOUNT_SUCCESS,
    responseBody,
    email,
    password,
    rememberMe,
});

export const createAccountFailure = () => ({
    type: CreateAccountActions.CREATE_ACCOUNT_FAILURE,
});

function _pad(d) {
    const i = Number(d);
    return i < 10 ? '0' + i.toString() : i.toString();
}

export const _toSnakeCase = (form) => {
    const month = _pad(form.birthMonth);
    const day = _pad(form.birthDay);
    return {
        accepted_terms_of_use: form.termsOfService,
        date_of_birth: `${form.birthYear}-${month}-${day}`,
        email: form.email,
        first_name: form.firstName,
        gender: form.gender,
        last_name: form.lastName,
        remember_me: form.rememberMe === undefined ? true : form.rememberMe,
        password: form.password,
        directory_id: form.directoryId,
    };
};

export const validateDate = async (year, month, day) => {
    const validator = getValidator('birthDate');
    return await validator([year, month, day]);
};

const getFormState = (state) => {
    return state.createAccountForm.form || {};
};

const getFormErrorState = (state) => {
    return state.createAccountForm.formErrors || {};
};

export const passwordStatusCheck = (password) => {
    return async (dispatch) => {
        const validator = getValidator('passwordStatusCheck');
        const statusCheck = await validator(password);

        dispatch(createAccountFormPasswordStatusCheck(statusCheck));
    };
};

export function fetchInlineEmailValidation(email) {
    return async (dispatch) => {
        const apiHost = process.env.API_HOST || '';
        const validationEndpoint = `${apiHost}/api/web/2/patient/validateemail`;
        const fetchData = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: email }),
            credentials: 'include',
        };
        dispatch(createAccountInlineEmailValidationStart());

        const resp = await fetch(validationEndpoint, fetchData).catch(() => {
            // handles request timeouts and CORS errors
        });

        if (resp.status === 400) {
            const val = await resp.json().catch(() => {});

            const fields = get(val, 'error.fields', []);
            const messages = fields.map((field) => field.message);

            if (messages.includes(EMAIL_TAKEN_MESSAGE)) {
                dispatch(createAccountEmailValidationTaken());
                dispatch(trackEmailInUseError());
            }
        }

        dispatch(createAccountInlineEmailValidationEnd());
    };
}

export function createAccountServerError(errorFields) {
    return (dispatch) => {
        errorFields.forEach((errorField) => {
            if (errorField.name === emailField) {
                dispatch(createAccountEmailValidationTaken());
                dispatch(trackEmailInUseError());
                dispatch(createAccountFormClosePasswordModal());
            }
        });
    };
}

export const validateField = (field, value) => {
    return async (dispatch, getState) => {
        const state = getState();
        const form = getFormState(state);

        const validate = async (field, ...values) => {
            const valid = await getValidator(field)(...values);

            if (field === emailField && valid) {
                dispatch(fetchInlineEmailValidation(value));
            }

            if (valid) {
                if (field === confirmEmailField) {
                    dispatch(clearConfirmEmailVaidationError());
                }
                return;
            }

            dispatch(createAccountValidationError(field));
            dispatch(trackError(field));
        };

        switch (field) {
            case emailField:
                await validate(field, value);
                await validate(
                    confirmEmailField,
                    form[confirmEmailField],
                    value
                );
                break;
            case confirmEmailField:
                await validate(field, value, form.email);
                break;
            case birthDayField:
            case birthYearField:
            case birthMonthField:
                await validate(field, value);
                break;
            case passwordField:
                dispatch(passwordStatusCheck(value));

                const checks = await getValidator('passwordStatusCheck')(value);
                let isValid = true;
                Object.keys(checks).forEach((key) => {
                    if (checks[key] === RULE_STATUS.INVALID) {
                        dispatch(trackV2PasswordError(key));
                        isValid = false;
                    }
                });

                if (!isValid) {
                    dispatch(createAccountValidationError(field));
                }

                return;

            default:
                await validate(field, value);
        }
    };
};

export const onBlur = (field, value) => {
    return async (dispatch) => {
        if (field === emailField) {
            dispatch(createAccountInlineEmailValidationClear());
        }
        dispatch(validateField(field, value));
    };
};

export const onFocus = (field) => {
    return (dispatch) => {
        dispatch(trackFocus(field));
        if (field === passwordField) {
            dispatch(createAccountFormShowInlinePasswordStatusCheck());
        }
    };
};

export const onChange = (field, value) => {
    return (dispatch) => {
        dispatch(createAccountFormChanged(field, value));

        const radioChange = trackRadio(field, value);
        if (radioChange) {
            dispatch(radioChange);
        }

        if (field === passwordField) {
            dispatch(passwordStatusCheck(value));
        }
    };
};

export const onSubmit = (mode) => {
    return async (dispatch, getState) => {
        dispatch(trackClickExpressSubmit());
        dispatch(createAccountSubmit());

        let valid = true;
        const state = getState();
        const form = getFormState(state);
        const errors = getFormErrorState(state);

        const fields = [
            emailField,
            confirmEmailField,
            firstNameField,
            lastNameField,
            birthDayField,
            birthMonthField,
            birthYearField,
            genderField,
            termsOfServiceField,
        ];

        const promises = fields.map(async (field) => {
            const value = form[field];
            const validator = getValidator(field);

            let isValid;

            if (field === confirmEmailField) {
                isValid = await validator(value, form[emailField]);
            } else {
                isValid = await validator(value);
            }

            if (!isValid) {
                await dispatch(createAccountValidationError(field));
                await dispatch(trackError(field));
            }

            if (field === emailField) {
                isValid = isValid && !errors.emailTaken;
            }

            valid = valid && isValid;
        });

        await Promise.all(promises);

        const isValidateDate = await validateDate(
            form.birthYear,
            form.birthMonth,
            form.birthDay
        );

        valid = valid && isValidateDate;

        const password = form[passwordField];
        const isPasswordValid = await getValidator(passwordField)(password);

        if (!valid) {
            return;
        }

        if (mode === SUBMIT_MODE.MODAL) {
            dispatch(createAccountFormShowPasswordModal());
            dispatch(trackPasswordModalShow());
        } else if (mode === SUBMIT_MODE.FULL) {
            if (isPasswordValid) {
                dispatch(createAccountStart());
                dispatch(
                    createAccountFetch({
                        ...form,
                        directoryId:
                            state[INITIAL_BOOKING_ROOT_KEY]?.directoryId,
                    })
                );
            } else {
                dispatch(passwordStatusCheck(password));
                dispatch(createAccountValidationError(passwordField));
                dispatch(trackError(passwordField));
            }
        }
    };
};

export function createAccountFetch(form) {
    let endpoint;

    const requestBody = _toSnakeCase(form);

    endpoint = standardEndpoint;

    return async (dispatch) => {
        const fetchData = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
            credentials: 'include',
        };
        let responseStatus;

        try {
            const response = await fetch(endpoint, fetchData);
            const body = await response.json();

            responseStatus = response.status;

            if (responseStatus === 201) {
                dispatch(
                    createAccountSuccess(
                        requestBody.email,
                        requestBody.password,
                        requestBody.remember_me,
                        body
                    )
                );
                return;
            }

            if (responseStatus === 400) {
                dispatch(createAccountServerError(body.error.fields));
            }
            dispatch(createAccountFailure());
            dispatch(trackAccountCreationFailure());
        } catch (e) {
            dispatch(createAccountFailure());
            dispatch(trackAccountCreationFailure());
        }
    };
}
